// Iter60 Task 2 — Admin Applications inbox
// Full list of partner/driver/franchisee applications with category filter,
// status pills, and one-click review actions (approve / deny / mark reviewing).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2, XCircle, Eye } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import SafeImage from "@/components/SafeImage";

const CATEGORY_LABEL = {
  product_brand: "Product Brand",
  store:         "Store",
  dispensary:    "Dispensary",
  glassblower:   "Glassblower / Glass Shop",
  vape_store:    "Vape-Specific Store",
  artist:        "Artist",
  other:         "Other Business",
};
const STATUS_CLS = {
  new:       "bg-amber-500/15 text-amber-300 border-amber-400/40",
  reviewing: "bg-sky-500/15 text-sky-300 border-sky-400/40",
  approved:  "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  denied:    "bg-rose-500/15 text-rose-300 border-rose-400/40",
};
const ROLE_FILTERS = [
  { v: "partner",   l: "Partner" },
  { v: "driver",    l: "Driver" },
  { v: "franchisee", l: "Franchisee" },
  { v: "",          l: "All" },
];
const STATUS_FILTERS = [
  { v: "",          l: "All" },
  { v: "new",       l: "New" },
  { v: "reviewing", l: "Reviewing" },
  { v: "approved",  l: "Approved" },
  { v: "denied",    l: "Denied" },
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminApplicationsInbox() {
  const [params] = useSearchParams();
  const focusId = params.get("focus");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Iter70 — pre-filter from URL: /admin/partners/applications, /admin/franchises/applications, /admin/applications?role=driver
  const initialRole = (() => {
    const r = params.get("role");
    if (r) return r;
    const path = (typeof window !== "undefined" ? window.location.pathname : "") || "";
    if (path.includes("/franchises/")) return "franchisee";
    if (path.includes("/drivers/") || path.includes("/driver-")) return "driver";
    return "partner";
  })();
  const [role, setRole] = useState(initialRole);
  const [status, setStatus] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/supplies/applications", {
        params: {
          ...(role   ? { role }   : {}),
          ...(status ? { status } : {}),
        },
      });
      setRows(data?.items || []);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [role, status]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, newStatus) => {
    setBusyId(id);
    try {
      await api.put(`/admin/supplies/applications/${id}/review`, { status: newStatus });
      await load();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const sorted = useMemo(() => {
    const arr = [...rows];
    if (focusId) {
      arr.sort((a, b) => (a.id === focusId ? -1 : b.id === focusId ? 1 : 0));
    }
    return arr;
  }, [rows, focusId]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-applications-page">
      <header>
        <div className="eyebrow">Admin · Master command</div>
        <h1 className="font-anton text-4xl uppercase text-white">Applications inbox</h1>
        <p className="text-lifted-dim text-sm mt-1 max-w-xl">
          Every partner, driver, and franchisee submission lands here for review.
          Partner applications include the selected business category.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-testid="admin-applications-filters">
        <div className="flex gap-1.5">
          {ROLE_FILTERS.map((r) => (
            <button
              key={r.v || "all"}
              onClick={() => setRole(r.v)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                role === r.v ? "bg-lifted-green/20 border-lifted-green/60 text-lifted-green" : "bg-white/5 border-white/10 text-lifted-dim hover:text-white hover:bg-white/10"
              }`}
              data-testid={`filter-role-${r.v || "all"}`}
            >
              {r.l}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.v || "all"}
              onClick={() => setStatus(s.v)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                status === s.v ? "bg-amber-400/20 border-amber-400/60 text-amber-300" : "bg-white/5 border-white/10 text-lifted-dim hover:text-white hover:bg-white/10"
              }`}
              data-testid={`filter-status-${s.v || "all"}`}
            >
              {s.l}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-lifted-dim font-mono self-center" data-testid="admin-applications-count">
          {rows.length} application{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading && (
        <div className="text-lifted-dim text-sm flex items-center gap-2" data-testid="admin-applications-loading">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </div>
      )}
      {err && !loading && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-3 py-2 flex items-center gap-2" data-testid="admin-applications-error">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {!loading && !err && sorted.length === 0 && (
        <div className="glass-panel p-6 text-center text-lifted-dim text-sm" data-testid="admin-applications-empty">
          No applications match the current filters.
        </div>
      )}

      <ul className="space-y-3" data-testid="admin-applications-rows">
        {sorted.map((r) => {
          const cat = r.business_category || (r.payload || {}).partner_type;
          const catLabel = CATEGORY_LABEL[cat] || (cat ? cat.replace(/_/g, " ") : "—");
          const statusKey = r.status || "new";
          const businessName = r.business_name
            || (r.payload || {}).store_name
            || (r.payload || {}).brand_name
            || (r.payload || {}).company_name
            || "—";
          const isFocused = focusId === r.id;
          return (
            <li
              key={r.id}
              className={`glass-panel p-4 ${isFocused ? "border-amber-400/60 shadow-[0_0_24px_-12px_rgba(251,191,36,0.6)]" : ""}`}
              data-testid={`admin-application-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                {/* Iter71 — show uploaded logo so admin can review it inline */}
                {r.logo_url && (
                  <a
                    href={r.logo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-black/40 border border-white/10 block"
                    data-testid={`admin-application-row-${r.id}-logo`}
                  >
                    <SafeImage
                      src={r.logo_url}
                      alt={`${r.business_name || r.name} logo`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bebas px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-lifted-dim">
                      {r.role}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.16em] font-bebas px-2 py-0.5 rounded-full border ${STATUS_CLS[statusKey] || STATUS_CLS.new}`}
                    >
                      {statusKey}
                    </span>
                  </div>
                  <h3 className="font-anton text-xl uppercase text-white">{r.name || "—"}</h3>
                  {/* Iter72 — current vs requested role surface so admin
                      can see at a glance that the applicant is already a
                      customer and what role they want upgraded to. */}
                  <div className="text-[11px] text-lifted-dim mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span><strong className="text-white">Current role:</strong>{" "}
                      {r.customer_user_id ? "customer" : "—"}</span>
                    <span><strong className="text-lifted-green">Requested role:</strong>{" "}
                      {r.role === "franchisee" ? "franchise" : r.role}</span>
                    {r.role === "partner" && r.business_category && (
                      <span><strong className="text-white">Subtype:</strong> {catLabel}</span>
                    )}
                  </div>
                  <div className="text-sm text-lifted-dim mt-1 flex flex-wrap gap-x-4">
                    <span><strong className="text-white">Business:</strong> {businessName}</span>
                  </div>
                  <div className="text-xs text-lifted-dim font-mono mt-1">
                    {r.email} · {r.phone || "—"} · {r.city || "—"}{r.state ? `, ${r.state}` : ""} · submitted {fmt(r.created_at)}
                  </div>
                  {r.notes && (
                    <details className="mt-2 text-xs text-lifted-dim">
                      <summary className="cursor-pointer hover:text-white">Notes</summary>
                      <p className="mt-1 text-white whitespace-pre-wrap">{r.notes}</p>
                    </details>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {statusKey === "new" && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => review(r.id, "reviewing")}
                      className="btn-ghost inline-flex items-center gap-1 text-xs"
                      data-testid={`app-mark-reviewing-${r.id}`}
                    >
                      <Eye size={11} /> Mark reviewing
                    </button>
                  )}
                  {statusKey !== "approved" && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => review(r.id, "approved")}
                      className="btn-lifted inline-flex items-center gap-1 text-xs"
                      data-testid={`app-approve-${r.id}`}
                    >
                      <CheckCircle2 size={11} /> Approve
                    </button>
                  )}
                  {statusKey !== "denied" && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => review(r.id, "denied")}
                      className="btn-ghost inline-flex items-center gap-1 text-xs hover:text-rose-300"
                      data-testid={`app-deny-${r.id}`}
                    >
                      <XCircle size={11} /> Deny
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
